import { Bell, HelpCircle, Wifi, WifiOff, ExternalLink } from 'lucide-react'
import { useState, useEffect } from 'react'
import useStore from '../../store/useStore'
import { getEndpointHealth } from '../../api/runpod'

const PAGE_TITLES = {
  'dashboard':         'Dashboard',
  'canvas':            'Canvas',
  'character-manager': 'Character Manager',
  'storyboard':        'Storyboard Builder',
  'clip-library':      'Clip Library',
  'image-library':     'Image Library',
  'settings':          'Settings',
}

export default function Header() {
  const { currentPage, notifications, clearNotification } = useStore()
  const [runpodOnline, setRunpodOnline] = useState(null)
  const [showNotifications, setShowNotifications] = useState(false)

  // Check RunPod health every 60 seconds
  useEffect(() => {
    const check = async () => {
      try {
        const { online } = await getEndpointHealth()
        setRunpodOnline(online)
      } catch {
        setRunpodOnline(false)
      }
    }
    check()
    const interval = setInterval(check, 60000)
    return () => clearInterval(interval)
  }, [])

  const unread = notifications.length

  return (
    <header className="h-14 flex-shrink-0 bg-surface-card border-b border-surface-border flex items-center justify-between px-6">

      {/* Page title */}
      <div>
        <h1 className="text-white font-semibold text-base">
          {PAGE_TITLES[currentPage] || currentPage}
        </h1>
      </div>

      {/* Right controls */}
      <div className="flex items-center gap-3">

        {/* RunPod status + Add Credits link */}
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface border border-surface-border">
          {runpodOnline === null ? (
            <Wifi size={14} className="text-slate-500 animate-pulse" />
          ) : runpodOnline ? (
            <Wifi size={14} className="text-green-400" />
          ) : (
            <WifiOff size={14} className="text-red-400" />
          )}
          <span className={`text-xs font-medium ${
            runpodOnline === null ? 'text-slate-500' :
            runpodOnline ? 'text-green-400' : 'text-red-400'
          }`}>
            {runpodOnline === null ? 'Checking...' : runpodOnline ? 'RunPod Online' : 'RunPod Offline'}
          </span>

          <span className="text-slate-600 text-xs">|</span>

          <a
            href="https://www.runpod.io/console/credits"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-brand-400 hover:text-brand-300 transition-colors"
            title="Add RunPod credits"
          >
            + Add Credits
            <ExternalLink size={10} />
          </a>
        </div>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative p-2 rounded-lg hover:bg-surface-hover text-slate-400 hover:text-slate-200 transition-colors"
          >
            <Bell size={17} />
            {unread > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-brand-500 rounded-full text-white text-xs flex items-center justify-center font-bold">
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </button>

          {showNotifications && (
            <div className="absolute right-0 top-full mt-2 w-80 bg-surface-card border border-surface-border rounded-xl shadow-2xl z-50">
              <div className="px-4 py-3 border-b border-surface-border flex items-center justify-between">
                <span className="text-sm font-medium text-white">Notifications</span>
                <button
                  onClick={() => notifications.forEach(n => clearNotification(n.id))}
                  className="text-xs text-slate-400 hover:text-slate-200"
                >
                  Clear all
                </button>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="px-4 py-6 text-center text-slate-500 text-sm">
                    No notifications
                  </div>
                ) : (
                  notifications.map(n => (
                    <div key={n.id} className="px-4 py-3 border-b border-surface-border hover:bg-surface-hover flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                        n.type === 'success' ? 'bg-green-400' :
                        n.type === 'error' ? 'bg-red-400' :
                        'bg-brand-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-200">{n.message}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {new Date(n.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                      <button
                        onClick={() => clearNotification(n.id)}
                        className="text-slate-500 hover:text-slate-300 flex-shrink-0"
                      >
                        ×
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Help */}
        <button className="p-2 rounded-lg hover:bg-surface-hover text-slate-400 hover:text-slate-200 transition-colors">
          <HelpCircle size={17} />
        </button>

      </div>
    </header>
  )
}
